import React, { useState, useRef } from 'react';
import { ICellRendererParams } from '@ag-grid-community/core';
import ProcessDiscoveryEmbedModal from './ProcessDiscoveryEmbedModal';
import { getPrimaryKeyValue } from 'common/visualizations/views/agGridReact/helpers/processDiscoveryHelper';
import type { ActionsConfigs } from 'common/visualizations/views/agGridReact/ProcessDiscoveryButton/types';
import { ForgePopover, ForgeIconButton, ForgeIcon } from '@tylertech/forge-react';
import ProcessDiscoveryContent from './ProcessDiscoveryContent';
import { v4 as uuidv4 } from 'uuid';

interface Props {
  rowParams: ICellRendererParams;
  processDiscoveryContext: ActionsConfigs;
  newTableActions: string[];
}

const ProcessDiscoveryViewButton: React.FC<Props> = ({
  rowParams,
  processDiscoveryContext,
  newTableActions,
}) => {
  const ActionsConfigs = processDiscoveryContext;
  const { mappedColumns } = ActionsConfigs;
  const mappedColumnsWithValue = mappedColumns
    ? mappedColumns.map((contextField) => {
        const value = getPrimaryKeyValue(rowParams, contextField.selectedColumn);
        return {
          ...contextField,
          value,
        };
      })
    : null;

  const uniqueActionKey = mappedColumnsWithValue
    ? mappedColumnsWithValue.map((col) => col.value).join('-')
    : undefined;
  const [showModal, setShowModal] = useState<boolean>(false);
  const [selectedUrl, setSelectedUrl] = useState<string>('');
  const targetRef = useRef<HTMLDivElement>(null);

  // Don't render button if no primary key value
  if (mappedColumns && mappedColumns.length === 0) {
    return null;
  }

  const onModalDismiss = () => {
    setShowModal(false);
    setSelectedUrl('');
  };

  const onLinkClick = (url: string) => {
    setShowModal(true);
    setSelectedUrl(url);
  };

  return (
    <div ref={targetRef} className="process-discovery-view">
      <ForgeIconButton
        id={`process-action-button-trigger-${uniqueActionKey}`}
        data-testid="process-action-button-id"
        className="tyler-icons"
        aria-label="process-discovery-view-button"
      >
        <ForgeIcon name="more_vert" />
      </ForgeIconButton>
      <ForgePopover
        targetElementRef={targetRef}
        anchor={`process-action-button-trigger-${uniqueActionKey}-${uuidv4()}`}
      >
        <div className="process-discovery-popup">
          <ProcessDiscoveryContent
            contextParams={{
              ...ActionsConfigs,
              mappedColumns: mappedColumnsWithValue,
            }}
            onLinkClick={onLinkClick}
            newTableActions={newTableActions}
          />
        </div>
      </ForgePopover>
      {showModal ? (
        <ProcessDiscoveryEmbedModal url={selectedUrl} onDismiss={onModalDismiss} showModal={showModal} />
      ) : null}
    </div>
  );
};

export default ProcessDiscoveryViewButton;
